import axios, { AxiosError } from 'axios';
import {
  defaultRequestInterceptors,
  defaultResponseInterceptors,
} from './config';

import { REQUEST_TIMEOUT } from '@/constants';
import { ElMessage } from 'element-plus';
import {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
  RequestConfig,
} from './types';

// 由環境變數中取得後端 API 路徑
export const PATH_URL = import.meta.env.VITE_XIN_TIAN_API_BASE_PATH;

// 用於管理可中斷請求的 AbortController 實例
const abortControllerMap: Map<string, AbortController> = new Map();

// 建立 axios 實例
const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL,
});

// 第一層 request 攔截器：負責建立並管理每個請求對應的 AbortController
axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const controller = new AbortController();
  const url = config.url || '';
  config.signal = controller.signal;
  abortControllerMap.set(url, controller);
  return config;
});

// 第一層 response 攔截器：負責在成功或失敗時清除對應的 AbortController
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const url = response.config.url || '';
    abortControllerMap.delete(url);
    // 這裡不做任何額外資料處理，留給後續的 defaultResponseInterceptors 處理
    return response;
  },
  (error: AxiosError) => {
    console.log('err：', error); // for debug

    // 如果是 422，表示有表單驗證類錯誤，需要將後端的錯誤訊息逐一顯示
    if (error.response?.status === 422) {
      // 這裡的型別因為不確定可自行修改 (any)
      const responseData: any = error.response.data;
      const validationErrors = responseData?.data?.result;

      // 如果後端有回傳明確的驗證錯誤細節 (validationErrors)
      if (validationErrors && typeof validationErrors === 'object') {
        // validationErrors 的結構為：
        // {
        //   account: ["請輸入帳號ID"],
        //   name: ["請輸入使用者名稱"],
        //   role_id: ["請選擇角色"]
        // }
        // 逐一取出 key 下陣列內的每個錯誤訊息，使用 ElMessage.error 跑出
        Object.keys(validationErrors).forEach((key) => {
          const messages = validationErrors[key];
          if (Array.isArray(messages)) {
            messages.forEach((msg: string) => {
              ElMessage.error(msg);
            });
          }
        });
      } else {
        // 如果沒有細節錯誤，只顯示後端的基本訊息或 axios 原本的錯誤訊息
        ElMessage.error(responseData?.message || error.message);
      }
    } else {
      // 其他錯誤狀況，使用 axios 原生錯誤訊息
      ElMessage.error(error.message);
    }

    // 錯誤拋出，讓後續 promise 有機會處理
    return Promise.reject(error);
  }
);

// 套用預設的 request 攔截器（可自定義加上 token、headers...等）
axiosInstance.interceptors.request.use(defaultRequestInterceptors);

// 套用預設的 response 攔截器（可統一處理後端返回的資料格式等）
axiosInstance.interceptors.response.use(defaultResponseInterceptors);

// 封裝的 service 物件，可提供呼叫 request、cancelRequest 等方法
const service = {
  // request 方法：支援可在傳入 config 時再加上自定義的攔截器
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      // 如果呼叫端有自定義 request 攔截器，在此先執行
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any);
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res);
        })
        .catch((err: any) => {
          reject(err);
        });
    });
  },

  // 取消指定 url 的請求
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url];
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort();
      abortControllerMap.delete(_url);
    }
  },

  // 取消當前所有正在執行的請求
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort();
    }
    abortControllerMap.clear();
  },
};

export default service;
